import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
    User,
    Building,
    FileText,
    Clock,
    CreditCard,
    AlertCircle,
    CheckCircle2
} from 'lucide-react';
import { getNotificationById, markNotificationAsRead } from '@/app/services/notificationService';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';

export default function NotificationDetailPage() {
    const { id } = useParams<{ id: string }>();
    const queryClient = useQueryClient();

    const { data: notification, isLoading } = useQuery({
        queryKey: ['notification', id],
        queryFn: () => getNotificationById(Number(id)),
        enabled: !!id,
    });

    const markReadMutation = useMutation({
        mutationFn: () => markNotificationAsRead(Number(id)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notification', id] });
        },
    });

    // Mark as read when viewing
    useEffect(() => {
        if (notification && !notification.read_at) {
            markReadMutation.mutate();
        }
    }, [notification]);

    if (isLoading) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Loading..." showBack />
                <div className="p-4 space-y-4">
                    <MobileSkeleton className="h-24 w-full" />
                    <MobileSkeleton className="h-48 w-full" />
                </div>
            </div>
        );
    }

    if (!notification) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Not Found" showBack />
                <div className="flex-1 flex items-center justify-center p-4">
                    <p className="text-muted-foreground">Notification not found</p>
                </div>
            </div>
        );
    }

    const data = notification.data || {};

    // Handle different field names from API
    const otpCode = data.otp || data.otp_code;
    const outstandingAmount = data.outstanding || data.outstanding_balance;

    const getStatusBadge = () => {
        if (notification.read_at) {
            return <MobileBadge variant="success">Read</MobileBadge>;
        }
        if (notification.status === 'pending') {
            return <MobileBadge variant="default">Pending</MobileBadge>;
        }
        return <MobileBadge variant="warning">Unread</MobileBadge>;
    };

    // Check if OTP is expired
    const isExpired = data.expires_at ? new Date(data.expires_at) < new Date() : false;

    // Parse credit info from body text if not in data
    const parseCreditInfo = () => {
        if (!notification.body) return null;

        const body = notification.body;
        const creditInfo: {
            outstanding?: number;
            invoiceAmount?: number;
            totalAfter?: number;
            creditLimit?: number;
            exceedsBy?: number;
        } = {};

        // Parse "Current Outstanding: ₹12,943.00"
        const outstandingMatch = body.match(/Current Outstanding:\s*₹?([\d,]+(?:\.\d{2})?)/);
        if (outstandingMatch) creditInfo.outstanding = parseFloat(outstandingMatch[1].replace(/,/g, ''));

        // Parse "New Invoice Amount: ₹1,000.00"
        const invoiceMatch = body.match(/New Invoice Amount:\s*₹?([\d,]+(?:\.\d{2})?)/);
        if (invoiceMatch) creditInfo.invoiceAmount = parseFloat(invoiceMatch[1].replace(/,/g, ''));

        // Parse "Total After Invoice: ₹13,943.00"
        const totalMatch = body.match(/Total After Invoice:\s*₹?([\d,]+(?:\.\d{2})?)/);
        if (totalMatch) creditInfo.totalAfter = parseFloat(totalMatch[1].replace(/,/g, ''));

        // Parse "Credit Limit: ₹5,000.00"
        const limitMatch = body.match(/Credit Limit:\s*₹?([\d,]+(?:\.\d{2})?)/);
        if (limitMatch) creditInfo.creditLimit = parseFloat(limitMatch[1].replace(/,/g, ''));

        // Parse "Exceeds limit by: ₹8,943.00"
        const exceedsMatch = body.match(/Exceeds limit by:\s*₹?([\d,]+(?:\.\d{2})?)/);
        if (exceedsMatch) creditInfo.exceedsBy = parseFloat(exceedsMatch[1].replace(/,/g, ''));

        return Object.keys(creditInfo).length > 0 ? creditInfo : null;
    };

    const creditInfo = parseCreditInfo();

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader title="OTP Request" showBack />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-6">
                    {/* Header card with OTP */}
                    <div className="mobile-card text-center py-6">
                        <p className="text-sm text-muted-foreground mb-2">
                            {notification.title}
                        </p>
                        {otpCode ? (
                            <>
                                <div className={`text-4xl font-bold tracking-[0.3em] mb-2 ${isExpired ? 'text-muted-foreground line-through' : 'text-primary'}`}>
                                    {otpCode}
                                </div>
                                {isExpired && (
                                    <p className="text-sm text-destructive mb-2">Expired</p>
                                )}
                                {!isExpired && data.expiry_minutes && (
                                    <p className="text-xs text-muted-foreground mb-2">
                                        Valid for {data.expiry_minutes} minutes
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className="text-lg text-muted-foreground mb-4">
                                No OTP code available
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-2">
                            {getStatusBadge()}
                            {isExpired && <MobileBadge variant="destructive">Expired</MobileBadge>}
                        </div>
                    </div>

                    {/* Party Info - Right below OTP */}
                    {data.party_name && (
                        <div className="mobile-card">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Customer</p>
                                    <p className="font-semibold text-lg">{data.party_name}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Credit Info Card */}
                    {creditInfo && (
                        <div>
                            <h2 className="mobile-section-header">Credit Overview</h2>
                            <div className="mobile-card space-y-4">
                                {/* Current Outstanding */}
                                {creditInfo.outstanding !== undefined && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">Current Outstanding</span>
                                        <span className="font-semibold">₹{creditInfo.outstanding.toLocaleString()}</span>
                                    </div>
                                )}

                                {/* New Invoice Amount */}
                                {creditInfo.invoiceAmount !== undefined && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">New Invoice Amount</span>
                                        <span className="font-semibold text-primary">+ ₹{creditInfo.invoiceAmount.toLocaleString()}</span>
                                    </div>
                                )}

                                {/* Divider */}
                                {(creditInfo.outstanding !== undefined || creditInfo.invoiceAmount !== undefined) && creditInfo.totalAfter !== undefined && (
                                    <div className="border-t border-border" />
                                )}

                                {/* Total After Invoice */}
                                {creditInfo.totalAfter !== undefined && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium">Total After Invoice</span>
                                        <span className="font-bold text-lg">₹{creditInfo.totalAfter.toLocaleString()}</span>
                                    </div>
                                )}

                                {/* Credit Limit */}
                                {creditInfo.creditLimit !== undefined && (
                                    <div className="flex justify-between items-center pt-2">
                                        <span className="text-sm text-muted-foreground">Credit Limit</span>
                                        <span className="font-semibold">₹{creditInfo.creditLimit.toLocaleString()}</span>
                                    </div>
                                )}

                                {/* Exceeds By - Warning */}
                                {creditInfo.exceedsBy !== undefined && creditInfo.exceedsBy > 0 && (
                                    <div className="flex justify-between items-center bg-destructive/10 -mx-4 px-4 py-3 mt-2 rounded-b-lg">
                                        <span className="text-sm font-medium text-destructive flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4" />
                                            Exceeds Limit By
                                        </span>
                                        <span className="font-bold text-destructive">₹{creditInfo.exceedsBy.toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Details section */}
                    <div>
                        <h2 className="mobile-section-header">Details</h2>
                        <MobileCardGroup>
                            {data.site_name && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <Building className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Site</p>
                                            <p className="font-medium">{data.site_name}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}

                            {data.expires_at && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <Clock className={`h-5 w-5 ${isExpired ? 'text-destructive' : 'text-muted-foreground'}`} />
                                        <div>
                                            <p className="text-xs text-muted-foreground">
                                                {isExpired ? 'Expired At' : 'Expires At'}
                                            </p>
                                            <p className={`font-medium ${isExpired ? 'text-destructive' : ''}`}>
                                                {format(new Date(data.expires_at), 'PPp')}
                                            </p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}

                            {data.requested_by && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <User className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Requested By</p>
                                            <p className="font-medium">{data.requested_by}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>
                    </div>

                    {/* Timestamps */}
                    <div>
                        <h2 className="mobile-section-header">Timeline</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <Clock className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Created</p>
                                        <p className="font-medium">
                                            {format(new Date(notification.created_at), 'PPp')}
                                        </p>
                                    </div>
                                </div>
                            </MobileCardRow>

                            {notification.read_at && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Read</p>
                                            <p className="font-medium">
                                                {format(new Date(notification.read_at), 'PPp')}
                                            </p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>
                    </div>

                    {/* Raw Message - at bottom for reference */}
                    {notification.body && (
                        <div>
                            <h2 className="mobile-section-header">Raw Message</h2>
                            <div className="mobile-card">
                                <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                                    {notification.body}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
